import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import type { Article } from '../contexts/LanguageContext';
import HeadJsonLd from '../components/HeadJsonLd';
import type { PublicPageData } from '../lib/publicPage';
import { supabase } from '../lib/supabase';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { Helmet } from 'react-helmet-async';
import { articlePath, articleSlugCandidates } from '../lib/articles';

type ArticleData = Article;

function DynamicArticleContent({ initialData }: { initialData?: PublicPageData | null }) {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t, articles } = useLanguage();
  const seededPage = initialData?.path === location.pathname;
  const seededArticle = initialData && initialData.path === location.pathname && slug
    ? initialData.articles.find(article => articleSlugCandidates(slug).includes(article.slug)) ?? null
    : null;
  const [article, setArticle] = React.useState<ArticleData | null>(seededArticle);
  const [loading, setLoading] = React.useState(!seededPage);
  const syncedArticleRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    document.body.setAttribute('data-lang', language);
    document.documentElement.setAttribute('lang', language === 'bg' ? 'bg' : 'en');
  }, [language]);

  // When language is switched while on an article page, find the counterpart and navigate to it
  React.useEffect(() => {
    if (!article) return;
    if (syncedArticleRef.current !== article.slug) {
      syncedArticleRef.current = article.slug;
      if (article.language === 'bg' || article.language === 'en') setLanguage(article.language);
      return;
    }
    const createdAt = article.created_at;
    if (article.language === language || !createdAt) return;

    const fetchCounterpart = async () => {
      const { data } = await supabase
        .from('articles')
        .select('slug')
        .eq('created_at', createdAt)
        .eq('language', language)
        .eq('published', true)
        .single();

      if (data?.slug) {
        // slug in DB includes blog/ prefix, strip it for the URL
        navigate(articlePath(data.slug), { replace: true });
      }
    };

    fetchCounterpart();
  }, [language, article, navigate, setLanguage]);

  React.useEffect(() => {
    if (seededPage) {
      setArticle(seededArticle);
      setLoading(false);
      return;
    }

    const fetchArticle = async () => {
      if (!slug) return;

      setLoading(true);
      setArticle(null);

      // Try with blog/ prefix first (all slugs in DB use this convention)
      let data: ArticleData | null = null;
      const candidates = articleSlugCandidates(slug);

      // Fallback: try the slug as-is (e.g. if stored without prefix)
      for (const candidate of candidates) {
        const result = await supabase
          .from('articles')
          .select('*')
          .eq('slug', candidate)
          .eq('published', true)
          .maybeSingle();
        if (result.data) {
          data = result.data as ArticleData;
          break;
        }
      }

      if (data) setArticle(data);
      setLoading(false);
    };

    fetchArticle();
  }, [slug, seededPage, seededArticle]);

  const alternateArticles = article?.created_at
    ? articles.filter(candidate => candidate.created_at === article.created_at)
    : [];
  const bulgarianArticle = alternateArticles.find(candidate => candidate.language === 'bg');
  const englishArticle = alternateArticles.find(candidate => candidate.language === 'en');
  const counterpartLanguage = article?.language === 'bg' ? 'en' : article?.language === 'en' ? 'bg' : undefined;
  const counterpartArticle = counterpartLanguage
    ? alternateArticles.find(candidate => candidate.published && candidate.language === counterpartLanguage)
    : undefined;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{article ? `${article.title} | ${article.language === 'bg' ? 'Адвокат Петър Николов' : 'Attorney Petar Nikolov'}` : loading ? (language === 'bg' ? 'Статия | Адвокат Петър Николов' : 'Article | Attorney Petar Nikolov') : (language === 'bg' ? 'Статията не е намерена | Адвокат Петър Николов' : 'Article Not Found | Attorney Petar Nikolov')}</title>
        {article && <meta name="description" content={article.description} />}
        {!loading && !article && <meta name="robots" content="noindex, nofollow" />}
        {article && <link rel="canonical" href={`https://advokatnikolov.com${articlePath(article.slug)}`} />}
        {article && bulgarianArticle && <link rel="alternate" hrefLang="bg" href={`https://advokatnikolov.com${articlePath(bulgarianArticle.slug)}`} />}
        {article && englishArticle && <link rel="alternate" hrefLang="en" href={`https://advokatnikolov.com${articlePath(englishArticle.slug)}`} />}
        {article && <link rel="alternate" hrefLang="x-default" href={`https://advokatnikolov.com${articlePath((englishArticle ?? article).slug)}`} />}
      </Helmet>
      {article && <HeadJsonLd id="article-jsonld" data={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        datePublished: article.created_at ?? article.date,
        inLanguage: article.language,
        mainEntityOfPage: `https://advokatnikolov.com${articlePath(article.slug)}`,
        author: { '@type': 'Person', name: 'Петър Николов', jobTitle: article.language === 'bg' ? 'Адвокат' : 'Attorney at Law' },
        publisher: {
          '@type': 'LegalService',
          name: article.language === 'bg' ? 'Адвокатска кантора Петър Николов' : 'Petar Nikolov Law Firm',
          url: 'https://advokatnikolov.com',
        },
      }} />}
      <NavBar />

      <section className="pt-32 pb-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="animate-pulse">
              <div className="h-4 bg-gray-100 rounded w-24 mb-6" />
              <div className="h-8 bg-gray-100 rounded w-3/4 mb-4" />
              <div className="h-8 bg-gray-100 rounded w-1/2 mb-12" />
              <div className="space-y-3">
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-4 bg-gray-100 rounded w-5/6" />
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-4 bg-gray-100 rounded w-4/6" />
              </div>
            </div>
          ) : article ? (
            <>
              <div className="flex items-center mb-6">
                <span className="text-gray-400">{article.date}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-serif text-navy mb-8">{article.title}</h1>
              <div
                className="article-content mb-12"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
              <div className="border-t border-gray-100 pt-6 flex flex-wrap items-center justify-between gap-4">
                <a href="/blog" className="text-gold font-semibold hover:text-gold/80 transition-colors">
                  ← {t('blog.viewAllArticles')}
                </a>
                {counterpartArticle && <a href={articlePath(counterpartArticle.slug)} aria-label={counterpartArticle.language === 'en' ? 'English version' : 'Българска версия'} className="inline-flex min-h-[44px] items-center font-semibold text-[#8a651a] underline decoration-gold/40 underline-offset-4 hover:text-navy">{counterpartArticle.language === 'en' ? 'English version' : 'Българска версия'}</a>}
                <button onClick={() => { window.location.href = '/#contact'; }} className="btn-primary text-sm">
                  {t('nav.consultation')}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center text-slate" role="status">
              <h1 className="font-serif text-3xl text-navy">{t('blog.notFound')}</h1>
              <a href="/blog" className="mt-6 inline-flex min-h-[44px] items-center font-semibold text-gold underline underline-offset-4">{t('blog.viewAllArticles')}</a>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default DynamicArticleContent;
